/*
 * Brake House Self-serve studded belt size checker (PDP)
 * --------------------------------------------------------
 * Belt sizing from waist input (jeans tag size OR measured waist circumference)
 * plus a snug/relaxed preference. Modelled on assets/bh-size-recommender.js (boots):
 * reuses the same hidden properties[_...] cart plumbing and the same .bhsr CSS.
 *
 * GEOMETRY (maker-confirmed 2026-07-16, same for every studded Western belt):
 *   - Buckle-center -> CENTER hole = belt size (inch) + 3 cm.
 *   - 5 holes, ~2.54 cm (1 inch) apart -> usable range = center +/- 5.08 cm.
 *   centerReach_cm(B) = B * 2.54 + 3
 *   hole i (1 tightest .. 5 loosest) circumference = centerReach + (i - 3) * 2.54
 *
 * RECOMMENDATION (Belt sizing logic v2, maker-confirmed):
 *   - Effective fastening length L = waist_cm + 4 cm. Leather is thick and the belt
 *     sits over the waistband, so the belt must be ~4 cm longer than the bare waist.
 *     (v1 matched the bare waist and recommended too small.)
 *   - STANDARD: the available size whose hole index is closest to the CENTER hole
 *     (= about the same number as the jeans size). Best-balanced everyday fit.
 *   - RELAXED: one size UP from Standard so the waist sits on the tighter holes near
 *     the buckle with room to loosen -- BUT only when that bigger size stays in range
 *     (hole index >= 1). If sizing up would be too loose (waist is on the snug side of
 *     Standard) or there is no bigger size, Relaxed keeps the Standard size and tells
 *     the shopper to fasten a hole or two looser instead.
 *   - EDGE: if even the best-centered size is out of range (waist smaller than the
 *     smallest belt or bigger than the biggest), we recommend nothing and point to CS.
 *   Physical holes: 1 = tightest, nearest the buckle; 5 = loosest, nearest the tip.
 *   For the same waist a smaller belt sits on the outer/tip holes, a bigger belt on the
 *   inner/buckle holes -- the copy explains this so the recommended hole makes sense.
 *   Maker example: 30 jeans (waist ~77cm, L~81cm) -> Standard 30, Relaxed 32.
 *
 * FIT ILLUSTRATION (added 2026-08-11):
 *   A to-scale SVG of the belt as worn: the hole being fastened, and how much tail is
 *   left past the buckle. It redraws whenever the shopper picks a different size chip, and
 *   for a size that cannot be fastened it says so and offers to switch back to our pick.
 *   Tail length comes from the published size chart (see TOTAL_LEN_* below) and is only ever
 *   drawn, never printed as a number. The illustration adds no new sizing rules, so the
 *   server-side mirrors below do not need to change.
 *   Styles live in assets/bh-belt-diagram.css, loaded by the two belt snippets.
 *
 * Mirror of the CS-dashboard engine: brake-house-ops/lib/belt-sizing.ts and the
 * Apps Script enforceBeltRules_. Keep the geometry + v2 recommendation in sync across all three.
 * The input sanity checks below (implausible measurement, jeans-vs-measured mismatch) are
 * UI-only double-checks and do not touch the geometry, so they need no server-side mirror.
 */
(function () {
  "use strict";
  // Korean storefront: Korean copy (sizing logic unchanged).
  var KO = typeof document !== "undefined" && /^ko/i.test(document.documentElement.lang || "");
  function T(en, ko) { return KO ? ko : en; }

  /* ============================ GEOMETRY + ALLOWANCE ============================ */

  var BELT = {
    CENTER_OFFSET_CM: 3,     // buckle-center -> center hole = size(inch) + this
    HOLE_SPACING_CM: 2.54,   // 1 inch between holes
    N_HOLES: 5,
    WAIST_ALLOWANCE_CM: 4    // leather thickness + worn over waistband (v2, maker-confirmed)
  };
  var CENTER_HOLE = (BELT.N_HOLES + 1) / 2; // = 3
  var IN_TO_CM = 2.54;

  // Input double-check thresholds (UI sanity only).
  var PLAUSIBLE_MIN_CM = 55;  // ~22 in, below this the measured number/unit is almost certainly wrong
  var PLAUSIBLE_MAX_CM = 130; // ~51 in, above this likewise (matches the cm range in UNIT_CFG)
  var MISMATCH_TOL_CM = 6;    // ~2.4 in. Beyond this, jeans vs measured is treated as an error: block and ask to re-measure.
  var NOTABLE_TOL_CM = 4;     // ~1.6 in. Between NOTABLE and MISMATCH: usable, but explain why the belt number differs from the jeans number and ask to confirm.

  // Total leather length, from the published size chart in the product metafield
  // custom.size_and_fit_html (28->90, 30->95 ... 42->125 cm) => size * 2.5 + 20.
  // Only used to draw the tail to scale in the fit illustration; never shown as a number,
  // because the chart's "buckle to centre hole = the size" wording and the maker-confirmed
  // CENTER_OFFSET_CM above disagree by 3cm. Cross-check: this formula puts the last hole
  // ~10.2-10.8cm from the tip on every size, which is the normal western tip length.
  var TOTAL_LEN_PER_INCH_CM = 2.5, TOTAL_LEN_BASE_CM = 20;

  function centerReachCm(sizeInch) {
    return sizeInch * IN_TO_CM + BELT.CENTER_OFFSET_CM;
  }
  function totalLengthCm(sizeInch) {
    return sizeInch * TOTAL_LEN_PER_INCH_CM + TOTAL_LEN_BASE_CM;
  }
  // Circumference reached at a given hole, and the leather left over past the buckle there.
  function reachAtHoleCm(sizeInch, hole) {
    return centerReachCm(sizeInch) + (hole - CENTER_HOLE) * BELT.HOLE_SPACING_CM;
  }
  function tailCm(sizeInch, hole) {
    return totalLengthCm(sizeInch) - reachAtHoleCm(sizeInch, hole);
  }
  function roundHole(h) {
    return Math.min(BELT.N_HOLES, Math.max(1, Math.round(h)));
  }
  // The circumference the customer actually fastens at (bare waist + leather allowance).
  function effectiveLengthCm(waistCm) {
    return waistCm + BELT.WAIST_ALLOWANCE_CM;
  }
  // Which hole (1 tightest .. 5 loosest, fractional) an effective length buckles at on a belt.
  function holeIndexFor(sizeInch, effLenCm) {
    return CENTER_HOLE + (effLenCm - centerReachCm(sizeInch)) / BELT.HOLE_SPACING_CM;
  }
  // Qualitative verdict for (size, effective length): below range / snug / ideal / roomy / above range.
  function verdictFor(sizeInch, effLenCm) {
    var h = holeIndexFor(sizeInch, effLenCm);
    if (h < 1) return "below range";   // belt too long: loose even at the tightest hole
    if (h > 5) return "above range";   // belt too short: won't reach even at the loosest hole
    if (h < 1.5) return "snug";        // hugging hole 1
    if (h > 4.5) return "roomy";       // hugging hole 5
    return "ideal";
  }

  /* ============================ RECOMMEND (v2) ============================ */

  // sizes: available belt sizes (inch numbers). preference: "snug" | "relaxed".
  // Returns a rich object the caller turns into copy, or null.
  //   { size, holeIndex, edge, stdSize, relaxedSize, relaxedSizedUp, holesToLoosen, holesToTighten }
  //   edge: "below" (waist < smallest belt) | "above" (waist > biggest belt) | null.
  function recommend(waistCm, sizes, preference) {
    if (!sizes || !sizes.length || waistCm == null) return null;
    var asc = sizes.slice().sort(function (a, b) { return a - b; });
    var L = effectiveLengthCm(waistCm);

    // STANDARD = available size whose hole index is closest to the center hole
    // (tie -> smaller = snugger). Equivalent to the size whose center reach is nearest L.
    var std = asc[0], bestD = Math.abs(holeIndexFor(asc[0], L) - CENTER_HOLE);
    asc.forEach(function (b) {
      var d = Math.abs(holeIndexFor(b, L) - CENTER_HOLE);
      if (d < bestD - 1e-9) { std = b; bestD = d; }
    });
    var stdH = holeIndexFor(std, L);

    // Waist beyond what we make: even the best-centered size is out of range.
    var edge = null;
    if (stdH > 5) edge = "above";      // biggest belt still won't reach
    else if (stdH < 1) edge = "below"; // smallest belt still loose at its tightest hole

    // RELAXED = one size up from Standard, but only if it stays in range (hole >= 1).
    // Otherwise (snug-side waist, or no bigger size) keep Standard and loosen by hole.
    var stdIdx = asc.indexOf(std);
    var relaxedSize = std, relaxedSizedUp = false;
    if (stdIdx < asc.length - 1) {
      var cand = asc[stdIdx + 1];
      if (holeIndexFor(cand, L) >= 1) { relaxedSize = cand; relaxedSizedUp = true; }
    }

    var pick = preference === "relaxed" ? relaxedSize : std;
    var h = holeIndexFor(pick, L);
    var hRounded = Math.min(BELT.N_HOLES, Math.max(1, Math.round(h)));
    return {
      size: pick,
      holeIndex: h,
      edge: edge,
      stdSize: std,
      relaxedSize: relaxedSize,
      relaxedSizedUp: relaxedSizedUp,
      holesToLoosen: BELT.N_HOLES - hRounded, // holes available toward the loose (tip) end
      holesToTighten: hRounded - 1            // holes available toward the tight (buckle) end
    };
  }

  // Plain-language description of where a hole sits, so the shopper understands the pick.
  //   h <= 2.4 -> tighter holes near the buckle; 2.4..3.6 -> middle; >= 3.6 -> looser holes near the tip.
  function holePhrase(h) {
    if (h < 2.4) return T("the tighter holes near the buckle", "버클 쪽 타이트한 구멍");
    if (h > 3.6) return T("the looser holes near the tip", "끝 쪽 느슨한 구멍");
    return T("the middle hole", "가운데 구멍");
  }
  function plural(n, word) { return n + " " + word + (n === 1 ? "" : "s"); }

  /* ============================ INPUT PARSING ============================ */

  function waistFromJeans(val) {
    var n = parseFloat(String(val || "").replace(/[^0-9.]/g, ""));
    return isNaN(n) ? null : n * IN_TO_CM;
  }
  function waistFromMeasure(val, unit) {
    var n = parseFloat(String(val || "").replace(",", "."));
    if (isNaN(n)) return null;
    return unit === "in" ? n * IN_TO_CM : n;
  }
  function holeLabel(holeIndex) {
    return KO ? BELT.N_HOLES + "개 중 " + roundHole(holeIndex) + "번째 구멍" : "hole " + roundHole(holeIndex) + " of " + BELT.N_HOLES;
  }

  /* ============================ FIT ILLUSTRATION (SVG) ============================
   * Worn view of the studded Western belt, drawn to scale from the geometry above:
   * the hole the shopper actually fastens, and how much tail is left past the buckle.
   *
   * The real concho buckle is nearly twice the belt width, so the neighbouring holes sit
   * behind it and genuinely cannot be seen when worn. A cutaway strip under the buckle
   * therefore shows all 5 holes, with a leader line back to the one in use.
   *
   * Product colours (black tooled leather, silver hardware, emerald/sapphire cabochons)
   * are literal, not theme tokens -- this is a rendering of the product, like a photo.
   */
  var VB_W = 520, VB_H = 268;
  var PX_PER_CM = 9;
  var HOLE_GAP = BELT.HOLE_SPACING_CM * PX_PER_CM;
  var BELT_TOP = 46, BELT_H = 40, BELT_MID = BELT_TOP + BELT_H / 2;
  var BUCKLE_CX = 260, BUCKLE_R = 38;   // 캔버스 정중앙
  var PRONG_X = 267;                 // the hole actually in use = the prong tip
  var STRAP_L = 0;
  var STRIP_TOP = 178, STRIP_H = 40; // cutaway under the buckle
  var BAND_TOP = 30, BAND_BOT = 92;  // jeans waistband
  var JEANS_BOT = 160;               // how far down the jeans are drawn

  var C = {
    toolLight: "#4C443D", toolDark: "#0E0C0A", braid: "#423B34",
    bevel: "#544B44", hole: "#050403", holeRim: "#6A6058",
    jewelA: "#2E7D4F", jewelB: "#2B4C9E",
    denimBand: "#3B587A", denimSeam: "#2A4064", stitch: "#D2A85F", rivet: "#C08A3E",
    annot: "#1E7A4F", annotSoft: "#6E727B", ink: "#1B1C20", warn: "#A93A29"
  };

  function svgEsc(s) { return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;"); }

  function toolPattern(id, originY) {
    return '<pattern id="' + id + '" patternUnits="userSpaceOnUse" x="0" y="' + originY + '" width="13" height="' + BELT_H + '">' +
      '<path d="M2 12 Q6.5 2 11 12" fill="none" stroke="' + C.toolLight + '" stroke-width="1.5"/>' +
      '<path d="M2 13.8 Q6.5 3.8 11 13.8" fill="none" stroke="' + C.toolDark + '" stroke-width="1" opacity=".8"/>' +
      '<path d="M2 28 Q6.5 38 11 28" fill="none" stroke="' + C.toolLight + '" stroke-width="1.5"/>' +
      '<path d="M2 26.2 Q6.5 36.2 11 26.2" fill="none" stroke="' + C.toolDark + '" stroke-width="1" opacity=".8"/>' +
      '<path d="M0 16.6 L6.5 20 L13 16.6" fill="none" stroke="' + C.braid + '" stroke-width="1.4"/>' +
      '<path d="M0 23.4 L6.5 20 L13 23.4" fill="none" stroke="' + C.braid + '" stroke-width="1.4"/>' +
      '</pattern>';
  }

  // Gradient/pattern ids are namespaced per widget instance so two widgets on one page
  // (PDP block + Size Guide block) cannot steal each other's fills.
  function svgDefs(ns) {
    return '<defs>' +
      '<linearGradient id="' + ns + 'lthr" x1="0" y1="0" x2="0" y2="1">' +
        '<stop offset="0" stop-color="#2C2723"/><stop offset=".5" stop-color="#1A1613"/>' +
        '<stop offset="1" stop-color="#0E0C0A"/></linearGradient>' +
      '<radialGradient id="' + ns + 'silv" cx="34%" cy="26%" r="78%">' +
        '<stop offset="0" stop-color="#EDEEF1"/><stop offset=".42" stop-color="#C2C4CA"/>' +
        '<stop offset=".82" stop-color="#94969D"/><stop offset="1" stop-color="#75777E"/></radialGradient>' +
      '<linearGradient id="' + ns + 'silvBar" x1="0" y1="0" x2="0" y2="1">' +
        '<stop offset="0" stop-color="#DEDFE3"/><stop offset=".5" stop-color="#AFB1B8"/>' +
        '<stop offset="1" stop-color="#7C7E85"/></linearGradient>' +
      '<radialGradient id="' + ns + 'dome" cx="34%" cy="28%" r="72%">' +
        '<stop offset="0" stop-color="#FFFFFF"/><stop offset=".45" stop-color="#D8D9DE"/>' +
        '<stop offset="1" stop-color="#8D8F96"/></radialGradient>' +
      // body strap curves away round the waist (darker); the tail hangs flat at the front
      '<linearGradient id="' + ns + 'wrap" x1="0" y1="0" x2="1" y2="0">' +
        '<stop offset="0" stop-color="#000" stop-opacity=".62"/>' +
        '<stop offset=".28" stop-color="#000" stop-opacity="0"/>' +
        '<stop offset=".85" stop-color="#000" stop-opacity="0"/>' +
        '<stop offset="1" stop-color="#000" stop-opacity=".18"/></linearGradient>' +
      '<linearGradient id="' + ns + 'bodyband" x1="0" y1="0" x2="1" y2="0">' +
        '<stop offset="0" stop-color="#2B4260"/><stop offset=".18" stop-color="#405F80"/>' +
        '<stop offset=".5" stop-color="#547396"/><stop offset=".82" stop-color="#405F80"/>' +
        '<stop offset="1" stop-color="#2B4260"/></linearGradient>' +
      '<linearGradient id="' + ns + 'drop" x1="0" y1="0" x2="0" y2="1">' +
        '<stop offset="0" stop-color="#000" stop-opacity=".3"/>' +
        '<stop offset="1" stop-color="#000" stop-opacity="0"/></linearGradient>' +
      toolPattern(ns + "tool", BELT_TOP) + toolPattern(ns + "tool2", STRIP_TOP) +
      '</defs>';
  }

  // The jeans the belt is worn over: indigo body, waistband, fly, front pockets, rivets.
  // The left-to-right gradient does the work of "wrapped around a waist"; the belt gets the
  // same treatment via the wrap overlay, so the two read as one curved surface.
  function jeans(ns) {
    var stitch = function (d) {
      return '<path d="' + d + '" fill="none" stroke="' + C.stitch + '" stroke-width="1" stroke-dasharray="5 5" opacity=".55"/>';
    };
    var p = [];
    // body + waistband
    p.push('<path d="M0 ' + (BAND_TOP + 7) + ' Q' + (VB_W / 2) + ' ' + (BAND_TOP - 5) + ' ' + VB_W + ' ' + (BAND_TOP + 7) +
      ' L' + VB_W + ' ' + JEANS_BOT + ' L0 ' + JEANS_BOT + ' Z" fill="url(#' + ns + 'bodyband)"/>');
    p.push('<path d="M0 ' + (BAND_TOP + 7) + ' Q' + (VB_W / 2) + ' ' + (BAND_TOP - 5) + ' ' + VB_W + ' ' + (BAND_TOP + 7) +
      ' L' + VB_W + ' ' + (BAND_BOT - 4) + ' Q' + (VB_W / 2) + ' ' + (BAND_BOT + 8) + ' 0 ' + (BAND_BOT - 4) +
      ' Z" fill="' + C.denimBand + '" opacity=".5"/>');
    // waistband top-stitching + seam under it
    p.push(stitch('M0 ' + (BAND_TOP + 13) + ' Q' + (VB_W / 2) + ' ' + (BAND_TOP + 1) + ' ' + VB_W + ' ' + (BAND_TOP + 13)));
    p.push(stitch('M0 ' + (BAND_BOT - 9) + ' Q' + (VB_W / 2) + ' ' + (BAND_BOT + 3) + ' ' + VB_W + ' ' + (BAND_BOT - 9)));
    p.push('<path d="M0 ' + (BAND_BOT - 4) + ' Q' + (VB_W / 2) + ' ' + (BAND_BOT + 8) + ' ' + VB_W + ' ' + (BAND_BOT - 4) +
      '" fill="none" stroke="' + C.denimSeam + '" stroke-width="1.4" opacity=".7"/>');
    // fly, centred under the buckle
    p.push('<path d="M' + (BUCKLE_CX - 9) + ' ' + (BAND_BOT + 2) + ' V' + JEANS_BOT +
      '" fill="none" stroke="' + C.denimSeam + '" stroke-width="1.6" opacity=".65"/>');
    p.push(stitch('M' + (BUCKLE_CX - 15) + ' ' + (BAND_BOT + 4) + ' V' + (JEANS_BOT - 6)));
    // front pockets + rivets
    [-1, 1].forEach(function (side) {
      var x0 = VB_W / 2 + side * 96;                 // pocket opening at the waistband
      var xEdge = side < 0 ? 0 : VB_W;               // runs off the frame, never stops mid-air
      p.push('<path d="M' + x0 + ' ' + (BAND_BOT + 1) + ' Q' + (x0 + side * 52) + ' ' + (BAND_BOT + 34) + ' ' + xEdge + ' ' + (BAND_BOT + 50) +
        '" fill="none" stroke="' + C.denimSeam + '" stroke-width="1.4" opacity=".55"/>');
      p.push(stitch('M' + (x0 + side * 6) + ' ' + (BAND_BOT + 3) + ' Q' + (x0 + side * 57) + ' ' + (BAND_BOT + 37) + ' ' + xEdge + ' ' + (BAND_BOT + 55)));
      p.push('<circle cx="' + x0 + '" cy="' + (BAND_BOT + 2) + '" r="2.4" fill="' + C.rivet + '"/>');
      p.push('<circle cx="' + (x0 - 0.7) + '" cy="' + (BAND_BOT + 1.2) + '" r="0.8" fill="#F0D9A8" opacity=".8"/>');
    });
    return p.join("");
  }

  // Jeans belt loops. Drawn AFTER the belt, because the belt passes through them.
  function beltLoops(ns) {
    // Ornaments sit at x = 20, 68, 116, 164 (see ornaments()); loops go in the gaps between
    // them so no stud cluster or cabochon is covered. The right-hand loop sits at 372 so the
    // tail threads through it at every wearable hole (shortest tail tip lands at ~359).
    var xs = [38, 134, 372], w = 11, top = BAND_TOP + 7, bot = BAND_BOT - 2, p = [];
    xs.forEach(function (x) {
      if (Math.abs(x + w / 2 - BUCKLE_CX) < BUCKLE_R + 14) return;   // never on top of the buckle
      p.push('<rect x="' + x + '" y="' + top + '" width="' + w + '" height="' + (bot - top) +
        '" rx="2" fill="' + C.denimBand + '" opacity=".92"/>');
      p.push('<rect x="' + x + '" y="' + top + '" width="' + w + '" height="' + (bot - top) +
        '" rx="2" fill="none" stroke="' + C.denimSeam + '" stroke-width=".9" opacity=".7"/>');
      p.push('<line x1="' + (x + 2.6) + '" y1="' + (top + 3) + '" x2="' + (x + 2.6) + '" y2="' + (bot - 3) +
        '" stroke="' + C.stitch + '" stroke-width=".9" stroke-dasharray="4 4" opacity=".5"/>');
      p.push('<line x1="' + (x + w - 2.6) + '" y1="' + (top + 3) + '" x2="' + (x + w - 2.6) + '" y2="' + (bot - 3) +
        '" stroke="' + C.stitch + '" stroke-width=".9" stroke-dasharray="4 4" opacity=".5"/>');
    });
    return p.join("");
  }

  function rectShape(x, w) {
    return function (fill) {
      return '<rect x="' + x + '" y="' + BELT_TOP + '" width="' + w + '" height="' + BELT_H + '" fill="' + fill + '"/>';
    };
  }
  function tailShape(x0, tipX) {
    var tipStart = tipX - 30;
    return function (fill) {
      return '<path d="M' + x0 + ' ' + BELT_TOP + ' H' + tipStart +
        ' Q' + tipX + ' ' + BELT_TOP + ' ' + tipX + ' ' + BELT_MID +
        ' Q' + tipX + ' ' + (BELT_TOP + BELT_H) + ' ' + tipStart + ' ' + (BELT_TOP + BELT_H) +
        ' H' + x0 + ' Z" fill="' + fill + '"/>';
    };
  }
  // base leather fill, then the tooling pattern over it
  function leather(shape, ns) {
    return shape("url(#" + ns + "lthr)") + shape("url(#" + ns + "tool)");
  }
  function bevel(x0, x1) {
    return '<line x1="' + x0 + '" y1="' + (BELT_TOP + 2.5) + '" x2="' + x1 + '" y2="' + (BELT_TOP + 2.5) +
      '" stroke="' + C.bevel + '" stroke-width="1" opacity=".55"/>' +
      '<line x1="' + x0 + '" y1="' + (BELT_TOP + BELT_H - 2.5) + '" x2="' + x1 + '" y2="' + (BELT_TOP + BELT_H - 2.5) +
      '" stroke="' + C.bevel + '" stroke-width="1" opacity=".55"/>';
  }

  // Diamond stud cluster: a 3x3 grid turned 45 degrees, nine domes.
  function studCluster(cx, cy, ns) {
    var d = 5.4, pts = [[0, -2 * d], [-d, -d], [d, -d], [-2 * d, 0], [0, 0], [2 * d, 0], [-d, d], [d, d], [0, 2 * d]];
    return pts.map(function (p) {
      var x = cx + p[0], y = cy + p[1];
      return '<circle cx="' + x + '" cy="' + y + '" r="2.9" fill="url(#' + ns + 'dome)"/>' +
        '<circle cx="' + (x - 0.85) + '" cy="' + (y - 0.95) + '" r="0.78" fill="#FFFFFF" opacity=".9"/>';
    }).join("");
  }
  function jewel(cx, cy, color, ns) {
    return '<circle cx="' + cx + '" cy="' + cy + '" r="6.8" fill="url(#' + ns + 'silv)"/>' +
      '<circle cx="' + cx + '" cy="' + cy + '" r="4.5" fill="' + color + '"/>' +
      '<circle cx="' + cx + '" cy="' + cy + '" r="4.5" fill="none" stroke="#000" stroke-width=".6" opacity=".4"/>' +
      '<ellipse cx="' + (cx - 1.4) + '" cy="' + (cy - 1.6) + '" rx="1.9" ry="1.2" fill="#FFFFFF" opacity=".65" ' +
      'transform="rotate(-30 ' + (cx - 1.4) + ' ' + (cy - 1.6) + ')"/>';
  }
  // Decoration sits on the body of the belt only -- the hole end is plain, as on the real product.
  // maxX lets the too-short drawing stop the decoration before the hole end, which moves left.
  function ornaments(ns, maxX) {
    var out = [], i = 0;
    var limit = Math.min(BUCKLE_CX - BUCKLE_R - 20, maxX == null ? Infinity : maxX);
    for (var x = 20; x < limit; x += 48, i++) {
      out.push(i % 2 === 0 ? studCluster(x, BELT_MID, ns) : jewel(x, BELT_MID, i % 4 === 1 ? C.jewelA : C.jewelB, ns));
    }
    return out.join("");
  }

  function punchedHole(cx, cy, scale) {
    var s = scale || 1, rx = 3.6 * s, ry = 4.3 * s;
    return '<ellipse cx="' + cx + '" cy="' + cy + '" rx="' + (rx + 1.3) + '" ry="' + (ry + 1.3) +
      '" fill="none" stroke="' + C.holeRim + '" stroke-width="1.5" opacity=".55"/>' +
      '<ellipse cx="' + cx + '" cy="' + cy + '" rx="' + rx + '" ry="' + ry + '" fill="' + C.hole + '"/>';
  }

  function conchoBuckle(ns) {
    var p = ['<g>'];
    p.push('<circle cx="' + BUCKLE_CX + '" cy="' + BELT_MID + '" r="' + BUCKLE_R + '" fill="url(#' + ns + 'silv)"/>');
    p.push('<circle cx="' + BUCKLE_CX + '" cy="' + BELT_MID + '" r="' + BUCKLE_R + '" fill="none" stroke="#6F7178" stroke-width=".8" opacity=".7"/>');
    for (var a = 0; a < 360; a += 9) {
      var rad = a * Math.PI / 180;
      p.push('<circle cx="' + (BUCKLE_CX + Math.cos(rad) * (BUCKLE_R - 3.2)).toFixed(1) +
        '" cy="' + (BELT_MID + Math.sin(rad) * (BUCKLE_R - 3.2)).toFixed(1) + '" r=".7" fill="#83858C" opacity=".6"/>');
    }
    [180, 0].forEach(function (base) {
      for (var k = -34; k <= 34; k += 8.5) {
        var r2 = (base + k) * Math.PI / 180;
        p.push('<line x1="' + (BUCKLE_CX + Math.cos(r2) * 16).toFixed(1) + '" y1="' + (BELT_MID + Math.sin(r2) * 16).toFixed(1) +
          '" x2="' + (BUCKLE_CX + Math.cos(r2) * 25).toFixed(1) + '" y2="' + (BELT_MID + Math.sin(r2) * 25).toFixed(1) +
          '" stroke="#7E8087" stroke-width=".9" opacity=".5"/>');
      }
    });
    [-1, 1].forEach(function (s) {
      p.push('<ellipse cx="' + BUCKLE_CX + '" cy="' + (BELT_MID + s * 22) + '" rx="9" ry="4" fill="none" stroke="#84868D" stroke-width="1.1" opacity=".85"/>');
      p.push('<ellipse cx="' + BUCKLE_CX + '" cy="' + (BELT_MID + s * 22) + '" rx="5.5" ry="2.2" fill="none" stroke="#9A9CA3" stroke-width=".8" opacity=".8"/>');
    });
    p.push('<rect x="' + (BUCKLE_CX - 17) + '" y="' + (BELT_TOP + 1) + '" width="34" height="' + (BELT_H - 2) + '" rx="2" fill="url(#' + ns + 'lthr)"/>');
    p.push('<rect x="' + (BUCKLE_CX - 17) + '" y="' + (BELT_TOP + 1) + '" width="34" height="' + (BELT_H - 2) + '" rx="2" fill="url(#' + ns + 'tool)"/>');
    p.push('<rect x="' + (BUCKLE_CX - 18.4) + '" y="' + (BELT_TOP - 0.4) + '" width="36.8" height="' + (BELT_H + 0.8) + '" rx="2.5" fill="none" stroke="#75777E" stroke-width="2"/>');
    p.push('</g>');
    return p.join("");
  }
  function prong(ns) {
    return '<rect x="' + (BUCKLE_CX - 17) + '" y="' + (BELT_MID - 1.7) + '" width="' + (PRONG_X - BUCKLE_CX + 18) +
      '" height="3.4" rx="1.7" fill="url(#' + ns + 'silvBar)"/>' +
      '<line x1="' + (BUCKLE_CX - 17) + '" y1="' + (BELT_MID + 2.1) + '" x2="' + PRONG_X + '" y2="' + (BELT_MID + 2.1) +
      '" stroke="#000" stroke-width=".9" opacity=".45"/>';
  }

  var STRIP_FONT_MONO = "ui-monospace,SFMono-Regular,Menlo,monospace";
  var STRIP_FONT_SANS = "-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif";

  // Cutaway of the strap under the buckle, so the holes hidden by the concho are still readable.
  // tone: undefined = normal, "loose" = belt too long, "short" = belt too short.
  function cutaway(hole, tone, ns, anchorX) {
    var accent = tone ? C.warn : C.annot;
    // `want` is where hole `hole` sits in the drawing above; `at` is where we can actually put
    // it without pushing the TIGHTEST/LOOSEST labels off the canvas. When they differ the leader
    // below becomes an elbow instead of a straight drop.
    var want = anchorX == null ? PRONG_X : anchorX;
    var LABEL_ROOM = 62, LEAD_IN = 30;
    var at = Math.min(
      Math.max(want, LABEL_ROOM + LEAD_IN + (hole - 1) * HOLE_GAP),
      VB_W - LABEL_ROOM - LEAD_IN - (BELT.N_HOLES - hole) * HOLE_GAP
    );
    var y = STRIP_TOP, mid = y + STRIP_H / 2, xs = [], i;
    for (i = 1; i <= BELT.N_HOLES; i++) xs.push(at + (i - hole) * HOLE_GAP);
    var x0 = xs[0] - 30, x1 = xs[BELT.N_HOLES - 1] + 30;
    var p = [];

    var leadTop = BELT_MID + (Math.abs(want - BUCKLE_CX) < BUCKLE_R ? BUCKLE_R : BELT_H / 2) + 2;
    p.push('<path d="M' + want + ' ' + leadTop + ' V' + (y - 22) + ' H' + at + ' V' + (y - 4) +
      '" fill="none" stroke="' + accent + '" stroke-width="1.2" stroke-dasharray="3 3"/>');
    p.push('<rect x="' + x0 + '" y="' + y + '" width="' + (x1 - x0) + '" height="' + STRIP_H + '" fill="url(#' + ns + 'lthr)"/>');
    p.push('<rect x="' + x0 + '" y="' + y + '" width="' + (x1 - x0) + '" height="' + STRIP_H + '" fill="url(#' + ns + 'tool2)"/>');
    p.push('<line x1="' + x0 + '" y1="' + (y + 2.5) + '" x2="' + x1 + '" y2="' + (y + 2.5) + '" stroke="' + C.bevel + '" opacity=".5"/>');
    p.push('<line x1="' + x0 + '" y1="' + (y + STRIP_H - 2.5) + '" x2="' + x1 + '" y2="' + (y + STRIP_H - 2.5) + '" stroke="' + C.bevel + '" opacity=".5"/>');
    p.push('<line x1="' + x0 + '" y1="' + y + '" x2="' + x0 + '" y2="' + (y + STRIP_H) + '" stroke="' + C.annotSoft + '" stroke-dasharray="3 3"/>');
    p.push('<line x1="' + x1 + '" y1="' + y + '" x2="' + x1 + '" y2="' + (y + STRIP_H) + '" stroke="' + C.annotSoft + '" stroke-dasharray="3 3"/>');

    for (i = 1; i <= BELT.N_HOLES; i++) {
      var cx = xs[i - 1], on = (i === hole);
      p.push(punchedHole(cx, mid, on ? 1.15 : 1));
      if (on) p.push('<ellipse cx="' + cx + '" cy="' + mid + '" rx="8.4" ry="9.6" fill="none" stroke="' + accent + '" stroke-width="2"/>');
      p.push('<text x="' + cx + '" y="' + (y + STRIP_H + 16) + '" text-anchor="middle" style="font-family:' + STRIP_FONT_MONO +
        ';font-size:11px;font-weight:' + (on ? 700 : 400) + ';fill:' + (on ? accent : C.annotSoft) + '">' + i + '</text>');
    }
    p.push('<text x="' + (x0 - 6) + '" y="' + (mid + 4) + '" text-anchor="end" style="font-family:' + STRIP_FONT_SANS +
      ';font-size:9.5px;letter-spacing:.1em;opacity:.85;fill:' + C.annotSoft + '">' + T("TIGHTEST", "가장 타이트") + '</text>');
    p.push('<text x="' + (x1 + 6) + '" y="' + (mid + 4) + '" style="font-family:' + STRIP_FONT_SANS +
      ';font-size:9.5px;letter-spacing:.1em;opacity:.85;fill:' + C.annotSoft + '">' + T("LOOSEST", "가장 느슨") + '</text>');
    p.push('<text x="' + at + '" y="' + (y + STRIP_H + 38) + '" text-anchor="middle" style="font-family:' + STRIP_FONT_SANS +
      ';font-size:13px;font-weight:600;fill:' + (tone ? C.warn : C.ink) + '">' +
      (tone === "loose" ? T("still loose at the tightest hole", "가장 타이트한 구멍에서도 헐렁함")
        : tone === "short" ? T("no hole reaches your waist", "허리에 닿는 구멍 없음")
        : svgEsc(holeLabel(hole))) + '</text>');
    return p.join("");
  }

  // Belt fastened at `holeRaw`. tone "loose" = even hole 1 leaves it slack.
  function wornSvg(size, holeRaw, tone, ns) {
    var warn = !!tone, hole = roundHole(holeRaw);
    var tipX = PRONG_X + tailCm(size, hole) * PX_PER_CM;
    var p = [];

    p.push('<svg viewBox="0 0 ' + VB_W + ' ' + VB_H + '" role="img" aria-label="' + (KO ? '사이즈 ' + size + ' 벨트를 ' + holeLabel(hole) + '에 채운 모습, 실제 비율' : 'Size ' + size + ' belt fastened at ' + holeLabel(hole) + ', drawn to scale') + '">');
    p.push(svgDefs(ns));
    p.push('<path d="M' + PRONG_X + ' 24 V17 H' + tipX + ' V24" fill="none" stroke="' + (warn ? C.warn : C.annotSoft) + '" stroke-width="1.2"/>');
    p.push('<text x="' + ((PRONG_X + tipX) / 2) + '" y="11" text-anchor="middle" style="font-family:' + STRIP_FONT_SANS +
      ';font-size:11px;letter-spacing:.07em;fill:' + (warn ? C.warn : C.annotSoft) + '">' +
      (warn ? T("FAR TOO MUCH TAIL", "꼬리가 너무 김") : T("TAIL PAST THE BUCKLE", "버클 지나 남는 꼬리")) + '</text>');

    p.push(jeans(ns));
    p.push('<rect x="0" y="' + (BELT_TOP + BELT_H) + '" width="' + tipX + '" height="7" fill="url(#' + ns + 'drop)"/>');

    var bodyStrap = rectShape(STRAP_L, BUCKLE_CX - STRAP_L + 4);
    var tailPath = tailShape(BUCKLE_CX - 4, tipX);
    p.push('<clipPath id="' + ns + 'clip">' + bodyStrap("#000") + tailPath("#000") + '</clipPath>');

    p.push(leather(bodyStrap, ns));
    p.push(bevel(STRAP_L, BUCKLE_CX));
    p.push(ornaments(ns));
    p.push(leather(tailPath, ns));
    p.push(bevel(BUCKLE_CX + BUCKLE_R, tipX - 30));
    for (var j = hole + 1; j <= BELT.N_HOLES; j++) {
      var hx = PRONG_X + (j - hole) * HOLE_GAP;
      if (hx < BUCKLE_CX + BUCKLE_R + 7 || hx > tipX - 34) continue;
      p.push(punchedHole(hx, BELT_MID));
    }
    p.push('<rect x="0" y="' + BELT_TOP + '" width="' + VB_W + '" height="' + BELT_H + '" fill="url(#' + ns + 'wrap)" clip-path="url(#' + ns + 'clip)"/>');
    p.push(beltLoops(ns));

    p.push(conchoBuckle(ns));
    p.push(punchedHole(PRONG_X, BELT_MID, 1.2));
    p.push(prong(ns));
    p.push(cutaway(hole, tone, ns));
    p.push('</svg>');
    return p.join("");
  }

  // Belt too short. The strap is ONE piece: it leaves the buckle, goes round the body, comes
  // back and enters the buckle from the left, and whatever is left sticks out to the right.
  // So the hole that would be used sits at distance `reach` along the strap while the prong
  // sits at distance L. When L > reach(hole 5) the nearest hole stops SHORT of the prong, i.e.
  // it lands to the LEFT of the buckle -- never to the right. (Drawing it on the right would
  // mean the hole had passed the prong, which is the too-LOOSE case, not too short.)
  //
  // Two situations fall out of the same drawing, because the tip is always
  // tailCm(size, 5) further along the strap than hole 5:
  //   small shortfall -> the leather still reaches round, a short tail pokes past the buckle,
  //                      but no hole lines up with the prong;
  //   big shortfall   -> the tip lands short of the buckle and you see a real gap.
  function tooShortSvg(size, h, ns) {
    var shortfallPx = (h - BELT.N_HOLES) * HOLE_GAP;
    // Keep hole 5 clear of the concho so the shortfall is always visible, and cap it so the
    // strap never runs off the left edge.
    var minShort = PRONG_X - (BUCKLE_CX - BUCKLE_R - 12);
    var hole5X = PRONG_X - Math.min(Math.max(shortfallPx, minShort), 160);
    var tipX = hole5X + tailCm(size, BELT.N_HOLES) * PX_PER_CM;
    var hole1X = hole5X - (BELT.N_HOLES - 1) * HOLE_GAP;
    var p = [];

    p.push('<svg viewBox="0 0 ' + VB_W + ' ' + VB_H + '" role="img" aria-label="' + (KO ? '사이즈 ' + size + '는 채워지지 않음: 가장 가까운 구멍이 버클에 못 미침' : 'Size ' + size + ' will not fasten: the nearest hole stops short of the buckle') + '">');
    p.push(svgDefs(ns));
    p.push('<path d="M' + hole5X + ' 24 V17 H' + PRONG_X + ' V24" fill="none" stroke="' + C.warn + '" stroke-width="1.4"/>');
    p.push('<text x="' + ((PRONG_X + hole5X) / 2) + '" y="11" text-anchor="middle" style="font-family:' + STRIP_FONT_SANS +
      ';font-size:11px;letter-spacing:.07em;font-weight:600;fill:' + C.warn + '">' + T("WON&#8217;T REACH", "닿지 않음") + '</text>');

    p.push(jeans(ns));
    p.push('<rect x="0" y="' + (BELT_TOP + BELT_H) + '" width="' + Math.max(tipX, 0) + '" height="7" fill="url(#' + ns + 'drop)"/>');

    var strap = tailShape(STRAP_L, tipX);
    p.push('<clipPath id="' + ns + 'clip">' + strap("#000") + '</clipPath>');
    p.push(leather(strap, ns));
    p.push(bevel(STRAP_L, tipX - 30));
    p.push(ornaments(ns, hole1X - 26));       // decoration never runs into the hole end
    for (var i = 1; i <= BELT.N_HOLES; i++) {
      var hx = hole5X - (BELT.N_HOLES - i) * HOLE_GAP;
      if (hx < 8 || hx > tipX - 30) continue;
      p.push(punchedHole(hx, BELT_MID, i === BELT.N_HOLES ? 1.15 : 1));
    }
    p.push('<rect x="0" y="' + BELT_TOP + '" width="' + VB_W + '" height="' + BELT_H + '" fill="url(#' + ns + 'wrap)" clip-path="url(#' + ns + 'clip)"/>');
    p.push(beltLoops(ns));

    // how far hole 5 falls short: from the hole to the buckle, then the prong with nothing in it
    p.push('<ellipse cx="' + hole5X + '" cy="' + BELT_MID + '" rx="8.4" ry="9.6" fill="none" stroke="' + C.warn + '" stroke-width="2"/>');
    // Only worth drawing when there is actually room for it; on a near miss the concho sits
    // almost on top of the hole and the bracket above the belt carries the message instead.
    var dashL = hole5X + 11, dashR = BUCKLE_CX - BUCKLE_R - 2;
    if (dashR - dashL >= 14) {
      p.push('<line x1="' + dashL + '" y1="' + BELT_MID + '" x2="' + dashR + '" y2="' + BELT_MID +
        '" stroke="' + C.warn + '" stroke-width="2" stroke-dasharray="7 6"/>');
    }

    p.push(conchoBuckle(ns));
    p.push(prong(ns));
    p.push(cutaway(BELT.N_HOLES, "short", ns, hole5X));
    p.push('</svg>');
    return p.join("");
  }

  // Which illustration applies for a size at effective length L.
  function illustrationFor(size, L, ns) {
    var h = holeIndexFor(size, L);
    if (h > BELT.N_HOLES) return { html: tooShortSvg(size, h, ns), wearable: false, tooShort: true, holeIndex: h };
    if (h < 1) return { html: wornSvg(size, h, "loose", ns), wearable: false, tooShort: false, holeIndex: h };
    return { html: wornSvg(size, h, null, ns), wearable: true, tooShort: false, holeIndex: h };
  }

  /* ============================ UI WIRING ============================ */

  var UNIT_CFG = {
    cm: { placeholder: "e.g. 84", min: "55", max: "130", range: "55-130 cm" },
    in: { placeholder: "e.g. 33", min: "22", max: "51",  range: "22-51 in" }
  };

  function findSizeControl() {
    var scope = document.querySelector('[data-wair-option-name="Size"]') || document;
    var chips = scope.querySelectorAll("button.dynamic-variant-button[data-option-value], button.product__chip[data-option-value]");
    if (chips.length) return { type: "chips", els: Array.prototype.slice.call(chips) };
    var select = scope.querySelector('select[name^="options"], select.dynamic-variant-input');
    if (select) return { type: "select", el: select };
    return null;
  }
  function readSizeOptions(control) {
    var out = [];
    if (!control) return out;
    if (control.type === "select") {
      Array.prototype.slice.call(control.el.options).forEach(function (o) {
        var num = parseFloat(String(o.value).replace(/[^0-9.]/g, ""));
        if (!isNaN(num)) out.push({ value: o.value, num: num });
      });
    } else {
      control.els.forEach(function (b) {
        var v = b.getAttribute("data-option-value") || b.textContent.trim();
        var num = parseFloat(String(v).replace(/[^0-9.]/g, ""));
        if (!isNaN(num)) out.push({ value: v, num: num, el: b });
      });
    }
    return out;
  }
  function snapToOption(sizeNum, options) {
    if (!options.length || sizeNum == null) return null;
    var exact = options.filter(function (o) { return Math.abs(o.num - sizeNum) < 1e-9; });
    if (exact.length) return exact[0];
    var best = null, bestD = Infinity;
    options.forEach(function (o) {
      var d = Math.abs(o.num - sizeNum);
      if (d < bestD || (d === bestD && best && o.num > best.num)) { best = o; bestD = d; }
    });
    return best;
  }
  function applySelection(control, option) {
    if (!control || !option) return;
    if (control.type === "select") {
      control.el.value = option.value;
      control.el.dispatchEvent(new Event("change", { bubbles: true }));
    } else if (option.el) {
      option.el.click();
    }
  }

  var WIDGET_SEQ = 0;

  function init(root) {
    var jeansSelect = root.querySelector("[data-bhbelt-jeans]");
    var measureInput = root.querySelector("[data-bhbelt-measure]");
    var resultEl = root.querySelector("[data-bhbelt-result]");
    if (!resultEl) return;

    // ---- fit illustration state
    var diagramEl = root.querySelector("[data-bhbelt-diagram]");
    var ns = "bhb" + (++WIDGET_SEQ) + "_";   // unique gradient/pattern ids per widget
    var ctx = null;         // { L, options, control, rec } from the last successful run
    var suppressWatch = false;  // set while WE move the size chip, so the watcher ignores it

    function clearDiagram() {
      if (diagramEl) { diagramEl.innerHTML = ""; diagramEl.removeAttribute("data-state"); }
    }

    // Draw the belt as the shopper would wear it in the size currently selected.
    // Any size in range is drawn; out of range we say so and offer to switch back.
    function drawFor(sizeNum) {
      if (!diagramEl || !ctx || sizeNum == null || isNaN(sizeNum)) return;
      var art = illustrationFor(sizeNum, ctx.L, ns);
      var recSize = ctx.rec ? ctx.rec.size : null;
      var isRec = recSize != null && Math.abs(sizeNum - recSize) < 1e-9;
      var html = art.html;

      if (!art.wearable) {
        html += KO
          ? '<p class="bhsr__diagram-note bhsr__diagram-note--warn">사이즈 ' + sizeNum + ' 벨트는 허리에 맞지 않습니다. ' +
            (art.tooShort ? "가장 느슨한 구멍으로도 둘러지지 않습니다." : "가장 타이트한 구멍으로도 헐렁해서 흘러내립니다.") +
            (recSize != null ? ' 추천 사이즈는 <strong>' + recSize + '</strong>입니다.' +
              '<button type="button" class="bhsr__diagram-fix" data-bhbelt-fix="' + recSize + '">사이즈 ' + recSize + ' 선택</button>' : "") +
            '</p>'
          : '<p class="bhsr__diagram-note bhsr__diagram-note--warn">Size ' + sizeNum + ' won’t work at your waist. ' +
          (art.tooShort ? "Even on the loosest hole it won’t reach around."
                        : "Even on the tightest hole it would sit loose and slip.") +
          (recSize != null ? ' We recommend size <strong>' + recSize + '</strong>.' +
            '<button type="button" class="bhsr__diagram-fix" data-bhbelt-fix="' + recSize + '">Switch to size ' + recSize + '</button>' : "") +
          '</p>';
      } else if (!isRec && recSize != null) {
        html += KO
          ? '<p class="bhsr__diagram-note">' + (sizeNum > recSize
            ? "추천보다 긴 벨트라 타이트한 쪽 구멍에 채우게 되고, 버클 뒤로 남는 꼬리가 길어집니다."
            : "추천보다 짧은 벨트라 끝 쪽 구멍에 채우게 되고, 조절할 여유가 적습니다.") +
            ' 저희 추천은 사이즈 <strong>' + recSize + '</strong>입니다.' +
            '<button type="button" class="bhsr__diagram-fix" data-bhbelt-fix="' + recSize + '">사이즈 ' + recSize + ' 선택</button></p>'
          : '<p class="bhsr__diagram-note">This is ' + (sizeNum > recSize
          ? "a longer belt than we recommend, so it sits on the tighter holes and leaves more tail hanging past the buckle."
          : "a shorter belt than we recommend, so it sits closer to the tip with less room to adjust.") +
          ' Our pick for you is size <strong>' + recSize + '</strong>.' +
          '<button type="button" class="bhsr__diagram-fix" data-bhbelt-fix="' + recSize + '">Switch to size ' + recSize + '</button></p>';
      }

      diagramEl.innerHTML = html;
      diagramEl.setAttribute("data-state", art.wearable ? "ok" : "warn");
    }

    // Move the size chip/select for the shopper, without our own watcher reacting to it.
    function selectSize_(control, option) {
      if (!control || !option) return;
      suppressWatch = true;
      applySelection(control, option);
      setTimeout(function () { suppressWatch = false; }, 0);
    }

    // Standalone (Size Guide) mode: no product context / size chips. The buyable belt sizes come
    // from a data attribute, and we relocate the block to sit under its heading in the page body.
    var fallbackSizes = (root.getAttribute("data-bhbelt-sizes") || "")
      .split(",").map(function (s) { return parseFloat(s); })
      .filter(function (n) { return !isNaN(n); });
    var relSel = root.getAttribute("data-bhbelt-relocate");
    if (relSel) {
      var anchor = document.querySelector(relSel);
      if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(root, anchor.nextSibling);
    }

    // cm/inch unit toggle for the measured-waist field (default inch, primary market).
    var unit = "in";
    var unitBtns = Array.prototype.slice.call(root.querySelectorAll("[data-bhbelt-unit]"));
    function setUnit(u) {
      if (!UNIT_CFG[u]) return;
      unit = u;
      unitBtns.forEach(function (b) {
        var active = b.getAttribute("data-bhbelt-unit") === u;
        b.classList.toggle("is-active", active);
        b.setAttribute("aria-pressed", active ? "true" : "false");
      });
      if (measureInput) {
        measureInput.placeholder = KO ? UNIT_CFG[u].placeholder.replace("e.g.", "예:") : UNIT_CFG[u].placeholder;
        measureInput.min = UNIT_CFG[u].min;
        measureInput.max = UNIT_CFG[u].max;
      }
      run();
    }
    unitBtns.forEach(function (b) {
      b.addEventListener("click", function () { setUnit(b.getAttribute("data-bhbelt-unit")); });
    });
    if (KO) setUnit("cm");

    // snug / relaxed fit-preference toggle (default snug).
    var preference = "snug";
    var prefBtns = Array.prototype.slice.call(root.querySelectorAll("[data-bhbelt-pref]"));
    function setPref(p) {
      if (p !== "snug" && p !== "relaxed") return;
      preference = p;
      prefBtns.forEach(function (b) {
        var active = b.getAttribute("data-bhbelt-pref") === p;
        b.classList.toggle("is-active", active);
        b.setAttribute("aria-pressed", active ? "true" : "false");
      });
      run();
    }
    prefBtns.forEach(function (b) {
      b.addEventListener("click", function () { setPref(b.getAttribute("data-bhbelt-pref")); });
    });

    // ---- cart-line properties (hidden properties[_...] on the ATC form), same pattern as boots.
    function setCartProp_(form, key, value) {
      var input = form.querySelector('input[data-bhbelt-prop="' + key + '"]');
      if (!value) { if (input && input.parentNode) input.parentNode.removeChild(input); return; }
      if (!input) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = "properties[" + key + "]";
        input.setAttribute("data-bhbelt-prop", key);
        form.appendChild(input);
      }
      input.value = value;
    }
    function findForm_() {
      return root.closest('form[action*="/cart"]') || document.querySelector('form[action*="/cart/add"]');
    }
    // selectedSizeNum: what is actually in the cart line. The recommendation is kept separately,
    // so CS can see both what we suggested and what the shopper chose.
    function syncCartProps_(rec, selectedSizeNum) {
      var form = findForm_();
      if (!form) return;
      var checkHole = rec ? rec.holeIndex : null;
      if (rec && ctx && selectedSizeNum != null && !isNaN(selectedSizeNum)) {
        checkHole = holeIndexFor(selectedSizeNum, ctx.L);
      }
      setCartProp_(form, "_jeans_size", (jeansSelect && jeansSelect.value) ? jeansSelect.value : "");
      setCartProp_(form, "_waist_measure", (measureInput && measureInput.value) ? (measureInput.value + " " + unit) : "");
      setCartProp_(form, "_belt_fit_preference", rec ? preference : "");
      setCartProp_(form, "_recommended_belt_size", rec ? String(rec.snapValue != null ? rec.snapValue : rec.size) : "");
      setCartProp_(form, "_belt_fit_check", rec ? (preference + " / hole " + roundHole(checkHole) + " of " + BELT.N_HOLES) : "");   // always English: CS-facing
    }

    function run() {
      var measureRaw = measureInput ? String(measureInput.value).trim() : "";
      // Measured waist takes priority over the jeans tag when both are given (more precise).
      var measureCm = measureInput ? waistFromMeasure(measureInput.value, unit) : null;
      var jeansCm = jeansSelect ? waistFromJeans(jeansSelect.value) : null;

      // ---- Double-check the shopper's inputs before recommending; block on obvious errors ----
      var warnMsg = null;
      if (measureCm != null && (measureCm < PLAUSIBLE_MIN_CM || measureCm > PLAUSIBLE_MAX_CM)) {
        // Number or unit is almost certainly wrong (e.g. "32" typed while cm is selected).
        warnMsg = KO
          ? "입력하신 측정값이 맞지 않는 것 같습니다. 숫자와 단위(" + (unit === "in" ? "인치" : "cm") + ")를 다시 확인해 주세요. 보통 허리둘레는 " + UNIT_CFG[unit].range + " 정도입니다."
          : "That measurement looks off. Please double-check the number and the unit (" +
          (unit === "in" ? "inch" : "cm") + "). A typical waist is about " + UNIT_CFG[unit].range + ".";
      } else if (measureCm != null && jeansCm != null && Math.abs(measureCm - jeansCm) > MISMATCH_TOL_CM) {
        // Jeans tag size and measured waist are too far apart to both be right: ask to re-measure.
        var gap = unit === "in" ? Math.abs(measureCm - jeansCm) / IN_TO_CM : Math.abs(measureCm - jeansCm);
        warnMsg = KO
          ? "두 값이 맞지 않습니다. W" + jeansSelect.value + " 청바지를 고르셨는데 측정값은 " + measureRaw + " " + unit + "(약 " + gap.toFixed(1) + " " + unit + " 차이)입니다. 벨트가 지나가는 위치에서 줄자를 수평으로 몸에 닿게 두고 다시 재시고, 청바지 사이즈도 확인해 주세요. 두 값이 맞으면 사이즈를 추천해 드립니다."
          : "These don’t match: you picked W" + jeansSelect.value + " jeans but measured " +
          measureRaw + " " + unit + " (about " + gap.toFixed(1) + " " + unit + " apart). Please re-measure " +
          "around where the belt sits, keeping the tape level and snug, and double-check your jeans size. " +
          "Once the two line up we’ll recommend your size.";
      }
      if (warnMsg) {
        resultEl.className = "bhsr__result bhsr__result--warn";
        resultEl.innerHTML = '<div class="bhsr__note">' + warnMsg + "</div>";
        syncCartProps_(null);
        clearDiagram();
        ctx = null;
        return;
      }

      var waistCm = measureCm != null ? measureCm : jeansCm;
      if (waistCm == null) {
        resultEl.className = "bhsr__result";
        resultEl.innerHTML = "";
        syncCartProps_(null);
        clearDiagram();
        ctx = null;
        return;
      }

      var control = findSizeControl();
      var options = readSizeOptions(control);
      if (!options.length && fallbackSizes.length) {
        // Size Guide page: no variant picker, so recommend against the buyable sizes we were given.
        options = fallbackSizes.map(function (n) { return { value: String(n), num: n }; });
        control = null; // nothing to auto-select, and syncCartProps_ no-ops without a cart form
      }
      var sizes = options.map(function (o) { return o.num; });
      var rec = recommend(waistCm, sizes, preference);
      if (!rec) {
        resultEl.className = "bhsr__result";
        resultEl.innerHTML = "";
        syncCartProps_(null);
        clearDiagram();
        ctx = null;
        return;
      }
      // ---- Waist beyond our size range: recommend nothing, point to CS (no auto-select) ----
      if (rec.edge === "below") {
        resultEl.className = "bhsr__result bhsr__result--warn";
        resultEl.innerHTML = KO
          ? '<div class="bhsr__note">허리둘레가 저희 가장 작은 스터드 벨트(사이즈 ' + rec.stdSize + ')보다 작습니다. 가장 타이트한 구멍으로도 헐렁합니다. 문의해 주시면 도와드리겠습니다.</div>'
          : '<div class="bhsr__note">Your waist is smaller than our smallest studded belt (size ' +
          rec.stdSize + '). Even on the tightest hole it would sit loose. Please contact us and we\'ll help.</div>';
        syncCartProps_(null);
        clearDiagram();
        ctx = null;
        return;
      }
      if (rec.edge === "above") {
        resultEl.className = "bhsr__result bhsr__result--warn";
        resultEl.innerHTML = KO
          ? '<div class="bhsr__note">허리둘레가 저희 가장 큰 스터드 벨트(사이즈 ' + rec.stdSize + ')보다 큽니다. 가장 느슨한 구멍으로도 닿지 않습니다. cm 단위로 나오는 게리슨 벨트가 더 크게 나오니 문의해 주세요.</div>'
          : '<div class="bhsr__note">Your waist is larger than our largest studded belt (size ' +
          rec.stdSize + '). Even on the loosest hole it wouldn\'t reach. Our Garrison belts (sized in cm) run larger, so please contact us.</div>';
        syncCartProps_(null);
        clearDiagram();
        ctx = null;
        return;
      }

      var snap = snapToOption(rec.size, options);
      rec.snapValue = snap ? snap.value : rec.size;
      var relaxedVal = (snapToOption(rec.relaxedSize, options) || {}).value || rec.relaxedSize;
      var stdVal = (snapToOption(rec.stdSize, options) || {}).value || rec.stdSize;

      var lines = [];
      if (KO) {
        lines.push('<div class="bhsr__size">추천 사이즈는 <strong>' + rec.snapValue + '</strong>입니다(' + (preference === "relaxed" ? "여유 있는" : "기본") + ' 착용감 기준).</div>');
        var noteKo;
        if (preference === "relaxed") {
          if (rec.relaxedSizedUp) {
            noteKo = '이 벨트는 허리보다 조금 길어서 ' + holePhrase(rec.holeIndex) + '(' + holeLabel(rec.holeIndex) + ')에 채우게 되고, 풀 수 있는 구멍이 ' + rec.holesToLoosen + '개 남습니다. 겹쳐 입거나 여유가 필요할 때 좋습니다. (기본 사이즈 ' + stdVal + ' 벨트는 끝 쪽 느슨한 구멍에 채우게 됩니다.)';
          } else {
            noteKo = '이미 이 사이즈의 여유 있는 쪽이라 한두 구멍만 느슨하게(' + holeLabel(rec.holeIndex) + ' 근처) 채우시면 됩니다. 사이즈를 올리면 가장 타이트한 구멍으로도 헐렁해서 잡히지 않습니다.';
          }
        } else {
          if (rec.holeIndex > 3.6) {
            noteKo = '벨트가 허리와 가까워 ' + holePhrase(rec.holeIndex) + '(' + holeLabel(rec.holeIndex) + ')에 채우게 되고, 조일 수 있는 구멍이 ' + rec.holesToTighten + '개 있습니다.';
          } else if (rec.holeIndex < 2.4) {
            noteKo = holePhrase(rec.holeIndex) + '(' + holeLabel(rec.holeIndex) + ')에 채우게 되고, 풀 수 있는 구멍이 ' + rec.holesToLoosen + '개 있습니다.';
          } else {
            noteKo = holePhrase(rec.holeIndex) + '(' + holeLabel(rec.holeIndex) + ') 근처에 채우게 되고, 양쪽으로 조이거나 풀 구멍이 있습니다. 균형 잡힌 일상 착용감입니다.';
          }
          if (rec.relaxedSizedUp) {
            noteKo += ' 풀 여유를 더 원하시면 \'여유 있게\'로 바꿔 사이즈 ' + relaxedVal + ' 벨트를 버클 쪽 타이트한 구멍에 채우세요.';
          } else {
            noteKo += ' 더 여유를 원하시면 \'여유 있게\'로 바꾸고 느슨한 구멍을 쓰시면 됩니다.';
          }
        }
        lines.push('<div class="bhsr__note">' + noteKo + '</div>');
        if (jeansCm != null && measureCm != null) {
          var measCmKo = Math.round(measureCm), diffCmKo = Math.abs(measureCm - jeansCm), softKo;
          if (diffCmKo >= NOTABLE_TOL_CM) {
            softKo = '참고: 실측 허리(약 ' + measCmKo + 'cm)와 W' + jeansSelect.value + ' 청바지가 약 ' + Math.round(diffCmKo) + 'cm 차이 나서, 추천 벨트 사이즈가 청바지 숫자보다 크거나 작게 보일 수 있습니다. 실측값 기준으로 잡았으니 벨트 위치에서 정확히 쟀는지 확인해 주세요. 청바지 사이즈가 더 믿을 만하면 측정값을 지우시면 W' + jeansSelect.value + ' 기준으로 잡습니다.';
          } else {
            softKo = 'W' + jeansSelect.value + ' 청바지와 실측 약 ' + measCmKo + 'cm를 입력하셨고 두 값이 맞습니다. 실측값을 썼으니 주문 전에 둘 다 한 번 더 확인해 주세요.';
          }
          lines.push('<div class="bhsr__note bhsr__note--soft">' + softKo + '</div>');
        }
      } else {
        var fitWord = preference === "relaxed" ? "relaxed" : "standard";
        lines.push('<div class="bhsr__size">We recommend size <strong>' + rec.snapValue + '</strong> for a ' + fitWord + ' fit.</div>');

        var note;
        if (preference === "relaxed") {
          if (rec.relaxedSizedUp) {
            // Sized up: waist lands on the tighter (buckle) holes with room to loosen.
            note = 'This belt is a little longer than your waist, so you\'ll fasten on ' + holePhrase(rec.holeIndex) +
                   ' (' + holeLabel(rec.holeIndex) + '), leaving ' + plural(rec.holesToLoosen, 'hole') +
                   ' to loosen. Good for layering or extra room. (A standard size ' + stdVal +
                   ' would instead sit on the looser holes near the tip.)';
          } else {
            // Can't size up without going too loose: keep the standard size, loosen by hole.
            note = 'You\'re already on the roomy side of this size, so just fasten a hole or two looser (around ' +
                   holeLabel(rec.holeIndex) + ') for extra room. Sizing up would be too loose to hold even on the tightest hole.';
          }
        } else {
          if (rec.holeIndex > 3.6) {
            note = 'Because the belt is close to your waist, you\'ll fasten on ' + holePhrase(rec.holeIndex) +
                   ' (' + holeLabel(rec.holeIndex) + '), with ' + plural(rec.holesToTighten, 'hole') + ' to tighten.';
          } else if (rec.holeIndex < 2.4) {
            note = 'You\'ll fasten on ' + holePhrase(rec.holeIndex) + ' (' + holeLabel(rec.holeIndex) +
                   '), with ' + plural(rec.holesToLoosen, 'hole') + ' to loosen.';
          } else {
            note = 'You\'ll fasten around ' + holePhrase(rec.holeIndex) + ' (' + holeLabel(rec.holeIndex) +
                   '), with holes on either side to tighten or loosen. The balanced everyday fit.';
          }
          if (rec.relaxedSizedUp) {
            note += ' Want more room to loosen? Switch to Relaxed for size ' + relaxedVal +
                    ', worn on the tighter holes near the buckle.';
          } else {
            note += ' Prefer more room? Switch to Relaxed and just use a looser hole.';
          }
        }
        lines.push('<div class="bhsr__note">' + note + '</div>');

        // Both inputs given: say which we used. When they diverge noticeably (but not enough to
        // block), explain why the belt number can differ from the jeans number and ask to confirm.
        if (jeansCm != null && measureCm != null) {
          var measInch = unit === "in" ? measureRaw : (measureCm / IN_TO_CM).toFixed(1);
          var diffCm = Math.abs(measureCm - jeansCm);
          var soft;
          if (diffCm >= NOTABLE_TOL_CM) {
            soft = 'Note: your measured waist (~' + measInch + 'in) and your W' + jeansSelect.value +
              ' jeans differ by about ' + (diffCm / IN_TO_CM).toFixed(1) + 'in, so the recommended belt size can look ' +
              'higher or lower than your jeans number. We sized off the measurement, so please make sure it’s accurate ' +
              '(taken around where the belt sits). If your jeans size is the better guide, clear the measurement to use W' +
              jeansSelect.value + ' instead.';
          } else {
            soft = 'You entered W' + jeansSelect.value + ' jeans and ~' + measInch +
              'in measured, which line up. We used the measurement, so please double-check both before ordering.';
          }
          lines.push('<div class="bhsr__note bhsr__note--soft">' + soft + '</div>');
        }
      }

      resultEl.className = "bhsr__result bhsr__result--ok";
      resultEl.innerHTML = lines.join("");
      ctx = { L: effectiveLengthCm(waistCm), options: options, control: control, rec: rec };
      selectSize_(control, snap);
      var drawnSize = snap ? snap.num : rec.size;
      drawFor(drawnSize);
      syncCartProps_(rec, drawnSize);
    }

    if (jeansSelect) jeansSelect.addEventListener("change", run);
    if (measureInput) {
      measureInput.addEventListener("input", run);
      measureInput.addEventListener("change", run);
    }

    // ---- redraw when the shopper picks a size themselves -------------------------------
    // The chips live in the variant picker, outside this block, so we listen on the document
    // and filter to the same controls findSizeControl() uses. suppressWatch keeps our own
    // auto-select (selectSize_) from bouncing straight back in here.
    var CHIP_SEL = "button.dynamic-variant-button[data-option-value], button.product__chip[data-option-value]";
    var SELECT_SEL = 'select[name^="options"], select.dynamic-variant-input';

    function numFromValue_(v) {
      var n = parseFloat(String(v || "").replace(/[^0-9.]/g, ""));
      return isNaN(n) ? null : n;
    }

    document.addEventListener("click", function (e) {
      if (suppressWatch || !ctx || !e.target || !e.target.closest) return;
      var chip = e.target.closest(CHIP_SEL);
      if (!chip) return;
      var num = numFromValue_(chip.getAttribute("data-option-value") || chip.textContent);
      if (num == null) return;
      drawFor(num);
      syncCartProps_(ctx.rec, num);
    });

    document.addEventListener("change", function (e) {
      if (suppressWatch || !ctx || !e.target || !e.target.matches) return;
      if (!e.target.matches(SELECT_SEL)) return;
      var num = numFromValue_(e.target.value);
      if (num == null) return;
      drawFor(num);
      syncCartProps_(ctx.rec, num);
    });

    // "Switch to size N" inside the illustration puts the shopper back on our pick.
    if (diagramEl) {
      diagramEl.addEventListener("click", function (e) {
        var btn = e.target && e.target.closest ? e.target.closest("[data-bhbelt-fix]") : null;
        if (!btn || !ctx) return;
        var want = numFromValue_(btn.getAttribute("data-bhbelt-fix"));
        if (want == null) return;
        var opt = snapToOption(want, ctx.options);
        selectSize_(ctx.control, opt);
        drawFor(opt ? opt.num : want);
        syncCartProps_(ctx.rec, opt ? opt.num : want);
      });
    }
  }

  function boot() {
    var roots = document.querySelectorAll("[data-bhbelt-root]");
    Array.prototype.forEach.call(roots, function (r) {
      if (r.getAttribute("data-bhbelt-ready")) return;
      r.setAttribute("data-bhbelt-ready", "1");
      init(r);
    });
  }

  // Expose the pure engine for QA/console spot-checks.
  if (typeof window !== "undefined") {
    window.BHBeltSizeChecker = {
      recommend: recommend,
      centerReachCm: centerReachCm,
      effectiveLengthCm: effectiveLengthCm,
      holeIndexFor: holeIndexFor,
      verdictFor: verdictFor,
      waistFromJeans: waistFromJeans,
      totalLengthCm: totalLengthCm,
      tailCm: tailCm,
      roundHole: roundHole
    };
  }

  if (typeof document !== "undefined" && document.addEventListener) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", boot);
    } else {
      boot();
    }
    document.addEventListener("shopify:section:load", boot);
  }
})();
